import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { formatDateToLocal } from '../utils/date';
import { useCategoryStore } from './categoryStore';
import { useTaskStore } from './taskStore';
import { useSyncedBlockStore } from './syncedBlockStore';
import type { Template, DaySchedule, TimeBlock, SharedTemplatePayload } from '../types';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount, swappingAccount,
} from '../lib/accountStorage';
import { changedFields, createDocSync, type DocBase, type DocSync } from '../lib/syncDoc';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';
import { loadedFor, refreshBlobOnFocus } from '../lib/blobFocusRefresh';
import { sortBlocksByDay, DEFAULT_DAY_START } from '../utils/templateSchedule';
import { useLocationStore } from './locationStore';
import { resolveDayBlocks } from '../utils/prayerBlocks';
import type { VoluntaryPrayerKey } from '../utils/dayCycle';

// ─── Supabase sync (sync epic) ──────────────────────────────────────────────
// Templates + day-schedules sync as ONE blob per user (user_schedules.data). A
// single store subscription syncs on any templates/schedules change.
// selectedDate/Template are transient UI → not synced.
//
// ⚠️⚠️ Writes go through syncDoc (lib/syncDoc.ts), NOT a whole-row upsert. The
// old upsert wrote this tab's copy of every template and day, so a stale tab
// brought back templates deleted elsewhere and undid other devices' edits —
// automatically, too: the prayer relinks rewrite every template. Now a write
// re-reads the row and applies only the templates and days THIS tab changed
// (templates by id, days by date; a delete beats an edit).

const USER_SCHEDULES_TABLE = 'user_schedules';

type ScheduleDoc = { templates: Template[]; schedules: DaySchedule[] };

const pickSchedules = (s: Pick<ScheduleState, 'templates' | 'schedules'>): ScheduleDoc =>
  ({ templates: s.templates, schedules: s.schedules });

function hydrateSchedules(data: unknown): ScheduleDoc {
  const d = (data && typeof data === 'object' && !Array.isArray(data) ? data : {}) as Record<string, unknown>;
  return {
    templates: Array.isArray(d.templates) ? (d.templates as Template[]) : [],
    schedules: Array.isArray(d.schedules) ? (d.schedules as DaySchedule[]) : [],
  };
}

const scheduleSync: DocSync<ScheduleDoc> = createDocSync<ScheduleDoc>({
  transport: supabaseBlobTransport(USER_SCHEDULES_TABLE),
  owner: () => useScheduleStore.getState().cachedUserId,
  rules: { templates: { records: 'id' }, schedules: { records: 'date' } },
  hydrate: hydrateSchedules,
  read: () => pickSchedules(useScheduleStore.getState()),
  readBase: () => useScheduleStore.getState().syncBase,
  adopt: (doc, base) => useScheduleStore.setState((state: ScheduleState) => ({
    ...changedFields(pickSchedules(state), doc),
    syncBase: base,
  })),
  onError: (error) => console.error('Failed to sync schedules to Supabase:', error),
});

/** Await the pending schedules write (tests). */
export function flushScheduleSync(): Promise<void> {
  return scheduleSync.flush();
}

// A tab that comes back shows templates changed elsewhere (lib/blobFocusRefresh.ts).
refreshBlobOnFocus(USER_SCHEDULES_TABLE, () => scheduleSync.refresh(loadedFor(() => useScheduleStore.getState().initializedUserId)));

interface ScheduleState {
  templates: Template[];
  schedules: DaySchedule[];
  selectedDate: string;
  selectedTemplate: Template | null;
  initializedUserId: string | null;
  cachedUserId: string | null;
  /** The last cloud copy this tab agreed with (hashed per record) — see lib/syncDoc.ts. */
  syncBase: DocBase<ScheduleDoc> | null;
  initialize: () => Promise<void>;
  setSelectedDate: (date: Date) => void;
  setSelectedTemplate: (template: Template | null) => void;
  handleCreateTemplate: () => void;
  handleDuplicateTemplate: (template: Template) => void;
  /** Copy a shared (social-post) template into MY templates; returns the new template. */
  importSharedTemplate: (payload: SharedTemplatePayload) => Template;
  handleSaveTemplate: (template: Template) => void;
  handleDeleteTemplate: (templateId: string) => void;
  handleApplyTemplate: (date: Date, templateId: string) => void;
  /**
   * ⭐⭐ **ADD ONE BLOCK TO A DAY, WITHOUT REBUILDING IT.** The only other way to
   * put anything on a date is `handleApplyTemplate`, and that replaces the day's
   * blocks with the template's — a block somebody added by hand that morning is
   * dropped, and synced blocks mint real tasks. A day that has been partly lived
   * cannot be unwound: a completed task cannot be un-completed.
   *
   * ⚠️⚠️ So this appends and sorts, and touches nothing already there. It exists
   * because a Growth Profile places a session on a chosen day (D27) and must never
   * be able to take somebody's morning with it.
   *
   * ⚠️ Idempotent on `syncId`: placing the same session on the same day twice
   * leaves one block, so a double-press or a retried apply cannot duplicate it.
   */
  addBlockToDate: (date: string, block: Omit<TimeBlock, 'id'>) => string | null;
  handleDeleteSchedule: () => void;
  getCurrentSchedule: () => DaySchedule | null;
  /** Repoint every prayer block (templates + applied schedules) at `habitId`. */
  relinkPrayerBlocks: (habitId: string) => void;
  /** Repoint every voluntary block of `key` (Duha/Qiyam) at `habitId`. */
  relinkVoluntaryBlocks: (key: VoluntaryPrayerKey, habitId: string) => void;
}

// Helper function to sort blocks by time
function sortBlocksByTime(blocks: TimeBlock[]): TimeBlock[] {
  return [...blocks].sort((a, b) => {
    const [aHour, aMinute] = a.startTime.split(':').map(Number);
    const [bHour, bMinute] = b.startTime.split(':').map(Number);
    return (aHour * 60 + aMinute) - (bHour * 60 + bMinute);
  });
}

export const useScheduleStore = create<ScheduleState>()(
  persist<ScheduleState, [], [], any>(
    (rawSet, get) => {
      // ⚠️ The first edit of a tab whose load never finished records its base
      // (see the same wrapper in userStore).
      const set: typeof rawSet = (...args: Parameters<typeof rawSet>) => {
        if (get().syncBase === null) rawSet({ syncBase: scheduleSync.baseOf(pickSchedules(get())) });
        return rawSet(...args);
      };
      return {
      templates: [],
      schedules: [],
      selectedDate: formatDateToLocal(new Date()),
      selectedTemplate: null,
      initializedUserId: null,
      cachedUserId: null,
      syncBase: null,

      // Load-on-login from the user_schedules blob, keyed by user id (re-runs on
      // account switch). The cloud wins for every template and day this tab has
      // not changed since its base; a cache that is another account's is
      // replaced outright. Runs on the write chain, so no write interleaves.
      initialize: () => dedupeInit('user_schedules', () => scheduleSync.exclusive(async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          // Cache-first boot: while this user's local cache is fresh, skip the
          // cloud read entirely (write-through keeps cloud current meanwhile).
          if (get().cachedUserId === user.id && isCloudFresh('user_schedules', user.id)) {
            rawSet((state) => ({
              initializedUserId: user.id,
              syncBase: state.syncBase ?? scheduleSync.baseOf(pickSchedules(state)),
            }));
            return;
          }

          const { data, error } = await supabase
            .from(USER_SCHEDULES_TABLE)
            .select('*');
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const row = (data ?? [])[0];
          const cloudData = row && row.data && (Array.isArray(row.data.templates) || Array.isArray(row.data.schedules))
            ? row.data
            : null;
          const state = get();
          const cacheIsOurs = state.cachedUserId === null || state.cachedUserId === user.id;
          let unsaved = false;
          if (cacheIsOurs) unsaved = scheduleSync.reconcile(cloudData);
          else scheduleSync.replace(cloudData);
          scheduleSync.noteLoaded(row?.updated_at ?? null);
          rawSet({ initializedUserId: user.id, cachedUserId: user.id });
          markCloudFresh('user_schedules', user.id);
          if (unsaved) scheduleSync.schedule();
        } catch (error) {
          console.error('Failed to initialize schedules from Supabase:', error);
        }
      })),

      setSelectedDate(date) {
        set({ selectedDate: formatDateToLocal(date) });
      },

      setSelectedTemplate(template) {
        set({ selectedTemplate: template });
      },

      handleCreateTemplate() {
        // Start empty: the editor requires the user to set the day start + bedtime
        // before any blocks, then seeds the first block at the day-start time.
        const newTemplate: Template = {
          id: crypto.randomUUID(),
          name: 'New Template',
          blocks: [],
        };

        set((state) => ({
          templates: [...state.templates, newTemplate],
          selectedTemplate: newTemplate,
        }));
      },

      handleDuplicateTemplate(template) {
        const baseName = template.name.replace(/ \(\d+\)$/, '');
        const duplicates = get().templates
          .filter(t => t.name.startsWith(baseName))
          .length;
        
        // Duplicate blocks with new IDs but preserve templateBlockId
        const { addSyncedBlock } = useSyncedBlockStore.getState();
        const newBlocks = template.blocks.map(block => {
          const newBlock = {
            ...block,
            id: crypto.randomUUID(),
            templateBlockId: block.templateBlockId || crypto.randomUUID()
          };

          // Add to synced blocks and get new syncId
          const syncId = addSyncedBlock(newBlock, newBlock.name);
          newBlock.syncId = syncId;

          return newBlock;
        });

        const newTemplate: Template = {
          ...template,
          id: crypto.randomUUID(),
          name: `${baseName} (${duplicates + 1})`,
          blocks: sortBlocksByTime(newBlocks),
        };

        set((state) => ({
          templates: [...state.templates, newTemplate],
          selectedTemplate: newTemplate,
        }));
      },

      importSharedTemplate(payload) {
        const { addSyncedBlock } = useSyncedBlockStore.getState();

        const baseName = payload.name.replace(/ \(\d+\)$/, '');
        const copies = get().templates.filter(t => t.name.startsWith(baseName)).length;
        const name = copies > 0 ? `${baseName} (${copies + 1})` : baseName;

        // Shared blocks are plain time blocks (links/prayer stripped at share
        // time) — give each a fresh identity like handleDuplicateTemplate does.
        const blocks = sortBlocksByTime(payload.blocks.map(shared => {
          const newBlock: TimeBlock = {
            id: crypto.randomUUID(),
            templateBlockId: crypto.randomUUID(),
            name: shared.name,
            startTime: shared.startTime,
            endTime: shared.endTime,
            category: shared.category,
            color: shared.color,
            type: 'time',
            ...(shared.commute ? { commute: true } : {}),
          };
          newBlock.syncId = addSyncedBlock(newBlock, newBlock.name);
          return newBlock;
        }));

        const newTemplate: Template = {
          id: crypto.randomUUID(),
          name,
          blocks,
          dayStart: payload.dayStart,
          bedtime: payload.bedtime,
        };

        set((state) => ({
          templates: [...state.templates, newTemplate],
        }));

        return newTemplate;
      },

      handleSaveTemplate(updatedTemplate) {
        const { categories } = useCategoryStore.getState();
        const { addSyncedBlock } = useSyncedBlockStore.getState();
        const { tasks } = useTaskStore.getState();
        
        // Update block colors and ensure sync (ordered relative to the template's day start)
        const blocksWithUpdatedColors = sortBlocksByDay(
          updatedTemplate.blocks.map(block => {
            const updatedBlock = {
              ...block,
              color: categories.find(c => c.id === block.category)?.color || block.color,
              templateBlockId: block.templateBlockId || crypto.randomUUID()
            };

            // Ensure block is synced (prayer/voluntary blocks are dynamic — never library-synced)
            if (!updatedBlock.syncId && !updatedBlock.prayer && !updatedBlock.voluntary) {
              const syncId = addSyncedBlock(updatedBlock, updatedBlock.name);
              updatedBlock.syncId = syncId;

              // Get tasks for this block
              const blockTasks = tasks[updatedBlock.category]?.filter(task => 
                task.blockId === updatedBlock.id || 
                task.templateBlockId === updatedBlock.templateBlockId
              ) || [];

              if (blockTasks.length > 0) {
                // Sync tasks to the block
                const syncedBlockStore = useSyncedBlockStore.getState();
                syncedBlockStore.syncTasksToBlock(syncId, blockTasks);
              }
            }

            return updatedBlock;
          }),
          updatedTemplate.dayStart ?? DEFAULT_DAY_START
        );

        const templateWithUpdatedColors = {
          ...updatedTemplate,
          blocks: blocksWithUpdatedColors
        };

        const { location, prayer } = useLocationStore.getState();

        set((state) => {
          // Update template in templates array
          const updatedTemplates = state.templates.map((t) =>
            t.id === updatedTemplate.id ? templateWithUpdatedColors : t
          );

          // Update all schedules that use this template
          const updatedSchedules = state.schedules.map((schedule) => {
            if (schedule.templateId === updatedTemplate.id) {
              // Create a map of existing blocks by templateBlockId for quick lookup
              const existingBlocksMap = schedule.blocks.reduce((map, block) => {
                if (block.templateBlockId) {
                  map[block.templateBlockId] = block;
                }
                return map;
              }, {} as Record<string, TimeBlock>);

              // Update blocks while preserving IDs and sync info
              const updatedBlocks = templateWithUpdatedColors.blocks.map(templateBlock => {
                const existingBlock = templateBlock.templateBlockId ? existingBlocksMap[templateBlock.templateBlockId] : undefined;
                if (existingBlock) {
                  return {
                    ...templateBlock,
                    id: existingBlock.id,
                    syncId: existingBlock.syncId || templateBlock.syncId,
                  };
                }
                return {
                  ...templateBlock,
                  id: crypto.randomUUID(),
                };
              });

              return {
                ...schedule,
                blocks: resolveDayBlocks(updatedBlocks, schedule.date, location, prayer),
              };
            }
            return schedule;
          });

          return {
            templates: updatedTemplates,
            schedules: updatedSchedules,
            selectedTemplate: null,
          };
        });
      },

      handleDeleteTemplate(templateId) {
        const template = get().templates.find(t => t.id === templateId);
        if (!template) return;

        // Before deleting template, preserve task associations
        template.blocks.forEach(block => {
          if (block.syncId) {
            const taskStore = useTaskStore.getState();
            const blockTasks = taskStore.tasks[block.category]?.filter(task => 
              task.blockId === block.id ||
              task.templateBlockId === block.templateBlockId ||
              task.syncId === block.syncId
            ) || [];

            // Update tasks to maintain sync association
            blockTasks.forEach(task => {
              taskStore.updateTask(task.id, task.category ?? '', {
                blockId: undefined,
                templateBlockId: block.templateBlockId,
                syncId: block.syncId
              });
            });
          }
        });

        set((state) => ({
          templates: state.templates.filter((t) => t.id !== templateId),
          selectedTemplate: null,
        }));
      },

      handleApplyTemplate(date, templateId) {
        const { templates } = get();
        const template = templates.find((t) => t.id === templateId);
        if (!template) return;

        const dateStr = formatDateToLocal(date);
        const { categories } = useCategoryStore.getState();
        const { addSyncedBlock } = useSyncedBlockStore.getState();
        const taskStore = useTaskStore.getState();

        // Get existing schedule if any
        const existingSchedule = get().schedules.find(s => s.date === dateStr);
        const existingBlocksMap = existingSchedule?.blocks.reduce((map, block) => {
          if (block.templateBlockId) {
            map[block.templateBlockId] = block;
          }
          return map;
        }, {} as Record<string, TimeBlock>) || {};

        // Create new blocks while preserving existing ones
        const newBlocks = template.blocks.map(templateBlock => {
          const existingBlock = templateBlock.templateBlockId ? existingBlocksMap[templateBlock.templateBlockId] : undefined;
          const blockColor = categories.find(c => c.id === templateBlock.category)?.color || templateBlock.color;

          if (existingBlock) {
            // Preserve existing block's ID and sync info
            return {
              ...templateBlock,
              id: existingBlock.id,
              color: blockColor,
              syncId: existingBlock.syncId || templateBlock.syncId
            };
          }

          // Create new block
          const newBlock = {
            ...templateBlock,
            id: crypto.randomUUID(),
            color: blockColor,
            templateBlockId: templateBlock.templateBlockId || crypto.randomUUID()
          };

          // Ensure block is synced (prayer/voluntary blocks are dynamic — never library-synced)
          if (!newBlock.syncId && !newBlock.prayer && !newBlock.voluntary) {
            const syncId = addSyncedBlock(newBlock, newBlock.name);
            newBlock.syncId = syncId;

            // Get synced tasks
            const syncedBlockStore = useSyncedBlockStore.getState();
            const syncedBlock = syncedBlockStore.getSyncedBlock(syncId);
            
            if (syncedBlock) {
              // Create new instances of synced tasks
              syncedBlock.tasks.forEach(task => {
                const newTask = {
                  ...task,
                  id: crypto.randomUUID(),
                  blockId: newBlock.id,
                  templateBlockId: templateBlock.templateBlockId,
                  syncId: syncId,
                  status: 'scheduled' as const,
                  completed: false,
                  pointRecord: undefined
                };
                taskStore.addTask(newTask);
              });
            }
          }

          return newBlock;
        });

        // Resolve dynamic prayer blocks to concrete times for THIS day (drops any
        // unavailable at high latitude / when prayer or location config is missing).
        const { location, prayer } = useLocationStore.getState();
        const dayBlocks = resolveDayBlocks(newBlocks, dateStr, location, prayer);

        const newSchedule: DaySchedule = {
          date: dateStr,
          templateId,
          blocks: sortBlocksByTime(dayBlocks),
        };

        set((state) => ({
          schedules: state.schedules
            .filter((s) => s.date !== dateStr)
            .concat(newSchedule),
        }));
      },

      addBlockToDate(date, block) {
        const existing = get().schedules.find(s => s.date === date);
        /* ⚠️ A block already on this day with the same `syncId` is the same
           placement — return it rather than adding a second. */
        const already = block.syncId
          ? existing?.blocks.find(b => b.syncId === block.syncId)
          : undefined;
        if (already) return already.id;

        const made: TimeBlock = { ...block, id: crypto.randomUUID() } as TimeBlock;
        set(state => ({
          schedules: existing
            ? state.schedules.map(s => (s.date === date
              ? { ...s, blocks: sortBlocksByTime([...s.blocks, made]) }
              : s))
            /* ⚠️ A day with no schedule yet gets one with ONLY this block —
               `templateId` stays empty because no template was applied, and
               pretending otherwise would make the day look template-derived to
               every surface that reads it. */
            : state.schedules.concat({ date, templateId: '', blocks: [made] }),
        }));
        return made.id;
      },

      handleDeleteSchedule() {
        const dateStr = get().selectedDate;
        const schedule = get().schedules.find(s => s.date === dateStr);

        if (schedule) {
          // Preserve tasks before deleting schedule
          schedule.blocks.forEach(block => {
            if (block.syncId) {
              const taskStore = useTaskStore.getState();
              const blockTasks = taskStore.tasks[block.category]?.filter(task => 
                task.blockId === block.id ||
                task.templateBlockId === block.templateBlockId ||
                task.syncId === block.syncId
              ) || [];

              // Update tasks to maintain sync association
              blockTasks.forEach(task => {
                taskStore.updateTask(task.id, task.category ?? '', {
                  blockId: undefined,
                  templateBlockId: block.templateBlockId,
                  syncId: block.syncId
                });
              });
            }
          });
        }

        set((state) => ({
          schedules: state.schedules.filter(s => s.date !== dateStr),
        }));
      },

      getCurrentSchedule() {
        const dateStr = get().selectedDate;
        return get().schedules.find((schedule) => schedule.date === dateStr) || null;
      },

      relinkPrayerBlocks(habitId) {
        const relink = (blocks: TimeBlock[]) =>
          blocks.map((b) => (b.prayer ? { ...b, habitIds: [habitId] } : b));
        set((state) => ({
          templates: state.templates.map((t) => ({ ...t, blocks: relink(t.blocks) })),
          schedules: state.schedules.map((s) => ({ ...s, blocks: relink(s.blocks) })),
        }));
      },

      relinkVoluntaryBlocks(key, habitId) {
        const relink = (blocks: TimeBlock[]) =>
          blocks.map((b) => (b.voluntary === key ? { ...b, habitIds: [habitId] } : b));
        set((state) => ({
          templates: state.templates.map((t) => ({ ...t, blocks: relink(t.blocks) })),
          schedules: state.schedules.map((s) => ({ ...s, blocks: relink(s.blocks) })),
        }));
      },
      };
    },
    {
      name: 'schedule-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 4,
      // Persist data + cache owner; selectedDate/Template + initializedUserId
      // stay transient.
      partialize: (state: ScheduleState) => ({
        templates: state.templates,
        schedules: state.schedules,
        cachedUserId: state.cachedUserId,
        syncBase: state.syncBase,
      }) as any,
      migrate: (persistedState: any, version: number) => {
        if (version < 4) {
          return {
            templates: [],
            schedules: [],
            selectedDate: formatDateToLocal(new Date()),
            selectedTemplate: null
          } as unknown as ScheduleState;
        }
        return persistedState as ScheduleState;
      }
    }
  )
);

// Auto-sync to Supabase whenever templates/schedules change (covers all data
// mutations). Ref-equality skips transient changes (selectedDate/Template); a
// cloud copy being adopted is not a change of ours.
useScheduleStore.subscribe((state, prev) => {
  if (swappingAccount()) return; // a change of account swaps the saved copy in — not an edit
  if (scheduleSync.adopting) return;
  if (state.templates === prev.templates && state.schedules === prev.schedules) return;
  scheduleSync.schedule();
});

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: an account's edits whose
   write had not landed lived only until the next account's load replaced the copy, and the next
   account's edits made before its own load were merged into the last one's row by ITS next load. */
scopeToAccount(useScheduleStore);
